import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { ALLOWED_TIMEZONES, getTimezoneLabel } from '../utils/timezoneUtils';
import LiveTlbUsageMappingEditor from './LiveTlbUsageMappingEditor';
import HangarOperatorKeysPanel from './HangarOperatorKeysPanel';
import SafetyApiKeysPanel from './SafetyApiKeysPanel';

const ClientSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    timezone: 'UTC',
    storageTimezone: 'UTC',
    displayTimezone: 'Asia/Kolkata',
    currency: 'USD',
    expiryReminders: {
      reminderDays: [30, 60],
      notifyOnExpired: true
    },
    measurementUnits: {
      preference: 'PER_AIRCRAFT',
      systemWide: {
        FUEL: 'kg',
        ALTITUDE: 'ft',
        TEMPERATURE: '°C',
        PRESSURE: 'PSI',
        SPEED: 'knots'
      },
      natureOfFlightMapping: {
        'N': 'Normal Flight',
        'TF': 'Test Flight',
        'NF': 'Night Flying',
        'GR': 'Ground Running'
      }
    },
    formatting: {
      purchaseOrderFormat: {
        prefix: 'PO-',
        datePart: 'YEAR',
        separator: '-',
        sequenceDigits: 3,
        sequenceStart: 1
      },
      workOrderFormat: [
        { type: 'PLAIN_TEXT', value: 'WO-' },
        { type: 'SEQUENCE', start: 1, digits: 6 }
      ],
      serializedItemFormat: [
        { type: 'PLAIN_TEXT', value: 'SI-' },
        { type: 'SEQUENCE', start: 1, digits: 6 }
      ]
    },
    rosterPlanningDefaults: {
      plannedWorkDays: 20,
      plannedOffDays: 10
    },
    hangar: {
      productMode: 'SUITE',
      enableMroFacade: false,
      enableExternalJobs: false,
      externalCompletionPolicy: 'AUTO_AFTER_MRO_QM'
    },
    hrms: {
      enabled: false,
      maxDailyOtHours: 4,
      maxWeeklyOtHours: 12,
      enforceManagerTeams: false
    },
    itineraryBranding: {
      companyName: '',
      tagline: '',
      logoUrl: '',
      email: '',
      website: '',
      phone: ''
    }
  });
  const [newReminderDay, setNewReminderDay] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [newNatureCode, setNewNatureCode] = useState({ code: '', description: '' });
  const [poPreview, setPoPreview] = useState('');
  const { showToast } = useToast();
  const { user } = useAuth();

  const WO_SEGMENT_OPTIONS = [
    { value: 'PLAIN_TEXT', label: 'Fixed Text' },
    { value: 'USER_TEXT', label: 'User Typed Input' },
    { value: 'CURRENT_YEAR', label: 'Current Year' },
    { value: 'CURRENT_MONTH', label: 'Current Month' },
    { value: 'CURRENT_DAY', label: 'Current Date of the Month' },
    { value: 'AIRCRAFT_REGISTRATION', label: 'Aircraft Registration Number' },
    { value: 'AIRCRAFT_PRIMARY_MODEL', label: 'Aircraft Primary Model Name' },
    { value: 'AIRCRAFT_MODEL_VARIANT', label: 'Aircraft Model Variant Name' },
    { value: 'SEQUENCE', label: 'Auto-incremented number' }
  ];

  const SI_SEGMENT_OPTIONS = [
    { value: 'PLAIN_TEXT', label: 'Fixed Text' },
    { value: 'USER_TEXT', label: 'User Typed Input' },
    { value: 'CURRENT_YEAR', label: 'Current Year' },
    { value: 'CURRENT_MONTH', label: 'Current Month' },
    { value: 'CURRENT_DAY', label: 'Current Date of the Month' },
    { value: 'SEQUENCE', label: 'Auto-incremented number' }
  ];

  const getWorkOrderExamplePreview = (segments) => {
    const dummy = {
      year: '2026',
      month: '02',
      day: '09',
      registration: 'VT-GA',
      primaryModel: 'Bell 407',
      variant: '407GXi',
      sequence: (s) => String((s?.start ?? 1) + 0).padStart(Math.min(8, Math.max(1, s?.digits ?? 6)), '0')
    };
    if (!Array.isArray(segments) || segments.length === 0) return '—';
    return segments.map((seg) => {
      switch (seg.type) {
        case 'PLAIN_TEXT':
        case 'USER_TEXT':
          return (seg.value != null ? String(seg.value) : '') || '';
        case 'CURRENT_YEAR': return dummy.year;
        case 'CURRENT_MONTH': return dummy.month;
        case 'CURRENT_DAY': return dummy.day;
        case 'AIRCRAFT_REGISTRATION': return dummy.registration;
        case 'AIRCRAFT_PRIMARY_MODEL': return dummy.primaryModel;
        case 'AIRCRAFT_MODEL_VARIANT': return dummy.variant;
        case 'SEQUENCE': return dummy.sequence(seg);
        default: return '';
      }
    }).filter(Boolean).join('') || '—';
  };

  const workOrderFormat = settings.formatting?.workOrderFormat ?? [
    { type: 'PLAIN_TEXT', value: 'WO-' },
    { type: 'SEQUENCE', start: 1, digits: 6 }
  ];

  const serializedItemFormat = settings.formatting?.serializedItemFormat ?? [
    { type: 'PLAIN_TEXT', value: 'SI-' },
    { type: 'SEQUENCE', start: 1, digits: 6 }
  ];

  const updateWorkOrderSegment = (index, field, value) => {
    const next = [...workOrderFormat];
    if (!next[index]) return;
    if (field === 'type') {
      next[index] = { ...next[index], type: value };
      if ((value === 'PLAIN_TEXT' || value === 'USER_TEXT') && next[index].value === undefined) next[index].value = '';
      if (value === 'SEQUENCE') {
        next[index].start = next[index].start ?? 1;
        next[index].digits = next[index].digits ?? 6;
      }
    } else {
      next[index] = { ...next[index], [field]: value };
    }
    setSettings({
      ...settings,
      formatting: { ...settings.formatting, workOrderFormat: next }
    });
  };

  const updateSerializedItemSegment = (index, field, value) => {
    const next = [...serializedItemFormat];
    if (!next[index]) return;
    if (field === 'type') {
      next[index] = { ...next[index], type: value };
      if ((value === 'PLAIN_TEXT' || value === 'USER_TEXT') && next[index].value === undefined) next[index].value = '';
      if (value === 'SEQUENCE') {
        next[index].start = next[index].start ?? 1;
        next[index].digits = next[index].digits ?? 6;
      }
    } else {
      next[index] = { ...next[index], [field]: value };
    }
    setSettings({
      ...settings,
      formatting: { ...settings.formatting, serializedItemFormat: next }
    });
  };

  const addWorkOrderSegment = () => {
    setSettings({
      ...settings,
      formatting: {
        ...settings.formatting,
        workOrderFormat: [...workOrderFormat, { type: 'PLAIN_TEXT', value: '' }]
      }
    });
  };

  const addSerializedItemSegment = () => {
    setSettings({
      ...settings,
      formatting: {
        ...settings.formatting,
        serializedItemFormat: [...serializedItemFormat, { type: 'PLAIN_TEXT', value: '' }]
      }
    });
  };

  const removeWorkOrderSegment = (index) => {
    if (workOrderFormat.length <= 1) return;
    const next = workOrderFormat.filter((_, i) => i !== index);
    setSettings({
      ...settings,
      formatting: { ...settings.formatting, workOrderFormat: next }
    });
  };

  const removeSerializedItemSegment = (index) => {
    if (serializedItemFormat.length <= 1) return;
    const next = serializedItemFormat.filter((_, i) => i !== index);
    setSettings({
      ...settings,
      formatting: { ...settings.formatting, serializedItemFormat: next }
    });
  };

  const getSerializedItemExamplePreview = (segments) => {
    const dummy = {
      year: '2026',
      month: '02',
      day: '09',
      sequence: (s) => String((s?.start ?? 1) + 0).padStart(Math.min(8, Math.max(1, s?.digits ?? 6)), '0')
    };
    if (!Array.isArray(segments) || segments.length === 0) return '—';
    return segments.map((seg) => {
      switch (seg.type) {
        case 'PLAIN_TEXT':
        case 'USER_TEXT':
          return (seg.value != null ? String(seg.value) : '') || '';
        case 'CURRENT_YEAR': return dummy.year;
        case 'CURRENT_MONTH': return dummy.month;
        case 'CURRENT_DAY': return dummy.day;
        case 'SEQUENCE': return dummy.sequence(seg);
        default: return '';
      }
    }).filter(Boolean).join('') || '—';
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (tabValue !== 3) return;
    const fetchPreview = async () => {
      try {
        const res = await axios.get('/api/client/settings/next-po-preview');
        setPoPreview(res.data?.preview ?? '');
      } catch {
        setPoPreview('');
      }
    };
    fetchPreview();
  }, [tabValue]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/client/settings');
      setSettings(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching client settings:', error);
      setError('Failed to load settings. Please try again.');
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validate reminder days
      if (!settings.expiryReminders.reminderDays || settings.expiryReminders.reminderDays.length === 0) {
        setError('At least one reminder day must be configured.');
        return;
      }

      await axios.put('/api/client/settings', settings);
      setSuccess('Settings saved successfully!');
      showToast('Settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save settings. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddReminderDay = () => {
    const day = parseInt(newReminderDay);
    if (isNaN(day) || day < 1 || day > 365) {
      setError('Reminder day must be a number between 1 and 365.');
      return;
    }

    if (settings.expiryReminders.reminderDays.includes(day)) {
      setError('This reminder day is already added.');
      return;
    }

    const updatedDays = [...settings.expiryReminders.reminderDays, day]
      .sort((a, b) => b - a); // Sort descending

    setSettings({
      ...settings,
      expiryReminders: {
        ...settings.expiryReminders,
        reminderDays: updatedDays
      }
    });
    setNewReminderDay('');
    setError('');
  };

  const handleRemoveReminderDay = (day) => {
    const updatedDays = settings.expiryReminders.reminderDays.filter(d => d !== day);
    setSettings({
      ...settings,
      expiryReminders: {
        ...settings.expiryReminders,
        reminderDays: updatedDays
      }
    });
  };

  const handleNotifyOnExpiredChange = (event) => {
    setSettings({
      ...settings,
      expiryReminders: {
        ...settings.expiryReminders,
        notifyOnExpired: event.target.checked
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleAddNatureMapping = () => {
    if (!newNatureCode.code.trim() || !newNatureCode.description.trim()) {
      showToast('Both code and description are required', 'error');
      return;
    }
    if (settings.measurementUnits.natureOfFlightMapping[newNatureCode.code]) {
      showToast('This code already exists', 'error');
      return;
    }
    setSettings({
      ...settings,
      measurementUnits: {
        ...settings.measurementUnits,
        natureOfFlightMapping: {
          ...settings.measurementUnits.natureOfFlightMapping,
          [newNatureCode.code]: newNatureCode.description
        }
      }
    });
    setNewNatureCode({ code: '', description: '' });
  };

  const handleRemoveNatureMapping = (code) => {
    const newMapping = { ...settings.measurementUnits.natureOfFlightMapping };
    delete newMapping[code];
    setSettings({
      ...settings,
      measurementUnits: {
        ...settings.measurementUnits,
        natureOfFlightMapping: newMapping
      }
    });
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4">Client Settings</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Configure system settings for your organization
        </Typography>
      </Box>

      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab label="General" />
        <Tab label="Measurement Units" />
        <Tab label="Nature of Flight Mapping" />
        <Tab label="Formatting" />
        <Tab label="Live TLB mapping" />
        <Tab label="Hangar product" />
        <Tab label="Staff details" />
        <Tab label="Safety integration" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* General Tab */}
      {tabValue === 0 && (
        <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Expiry Reminder Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure when users and administrators should be notified about expiring certificates and trainings.
          Reminders will be sent to users about their own items, and to MIS, Accountable Manager, and Super Admin about all users' items.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Reminder Days (Days Before Expiry)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure the number of days before expiry to send reminders. For example, setting 30 and 60 will send reminders 30 days and 60 days before expiry.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            {settings.expiryReminders.reminderDays.map((day) => (
              <Chip
                key={day}
                label={`${day} days`}
                onDelete={() => handleRemoveReminderDay(day)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              type="number"
              label="Add Reminder Day"
              value={newReminderDay}
              onChange={(e) => setNewReminderDay(e.target.value)}
              inputProps={{ min: 1, max: 365 }}
              size="small"
              sx={{ width: 200 }}
              helperText="Enter days (1-365)"
            />
            <IconButton
              color="primary"
              onClick={handleAddReminderDay}
              disabled={!newReminderDay}
            >
              <Add />
            </IconButton>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        <FormControlLabel
          control={
            <Switch
              checked={settings.expiryReminders.notifyOnExpired}
              onChange={handleNotifyOnExpiredChange}
            />
          }
          label="Notify when items have expired"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
          When enabled, notifications will be sent for items that have already expired (in addition to items expiring soon).
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          General Settings
        </Typography>

        <Stack spacing={3} sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Storage timezone (saved in DB)</InputLabel>
            <Select
              value={settings.storageTimezone ?? settings.timezone ?? 'UTC'}
              label="Storage timezone (saved in DB)"
              onChange={(e) => setSettings({ ...settings, storageTimezone: e.target.value })}
            >
              {ALLOWED_TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {getTimezoneLabel(tz)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Display timezone (shown in UI)</InputLabel>
            <Select
              value={settings.displayTimezone ?? 'Asia/Kolkata'}
              label="Display timezone (shown in UI)"
              onChange={(e) => setSettings({ ...settings, displayTimezone: e.target.value })}
            >
              {ALLOWED_TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {getTimezoneLabel(tz)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Dates/times are stored in the storage timezone (e.g. UTC). They are displayed and entered in the display timezone (e.g. IST). This applies to Flight Entry (charter/sector times), Crew Duty, Crew Availability & Leave, Crew Assignments, Technical Log Book (dates; rotor times are UTC only), Company Policy, Maintenance, Aircraft/AMP, Store/Procurement, Parts (expiry), and Feedback. Technical Log Book rotor start/stop times are stored and displayed in UTC only (see TLB screens).
          </Typography>
          <TextField
            fullWidth
            label="Currency"
            value={settings.currency}
            onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
            helperText="Default currency code (e.g., USD, EUR, INR)"
          />
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Passenger Itinerary Branding
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Shown on passenger journey itinerary PDFs (company header and footer). Leave blank to use client contact defaults / built-in fallback.
        </Typography>
        <Stack spacing={2} sx={{ mt: 1, maxWidth: 560 }}>
          <TextField
            fullWidth
            label="Company Name"
            value={settings.itineraryBranding?.companyName || ''}
            onChange={(e) => setSettings({
              ...settings,
              itineraryBranding: { ...(settings.itineraryBranding || {}), companyName: e.target.value }
            })}
            placeholder="e.g. Fleetwright India Private Limited"
          />
          <TextField
            fullWidth
            label="Tagline"
            value={settings.itineraryBranding?.tagline || ''}
            onChange={(e) => setSettings({
              ...settings,
              itineraryBranding: { ...(settings.itineraryBranding || {}), tagline: e.target.value }
            })}
            placeholder='e.g. "Precision in Flight"'
          />
          <TextField
            fullWidth
            label="Logo URL"
            value={settings.itineraryBranding?.logoUrl || ''}
            onChange={(e) => setSettings({
              ...settings,
              itineraryBranding: { ...(settings.itineraryBranding || {}), logoUrl: e.target.value }
            })}
          />
          <TextField
            fullWidth
            label="Email"
            value={settings.itineraryBranding?.email || ''}
            onChange={(e) => setSettings({
              ...settings,
              itineraryBranding: { ...(settings.itineraryBranding || {}), email: e.target.value }
            })}
            placeholder="ops@example.com"
          />
          <TextField
            fullWidth
            label="Website"
            value={settings.itineraryBranding?.website || ''}
            onChange={(e) => setSettings({
              ...settings,
              itineraryBranding: { ...(settings.itineraryBranding || {}), website: e.target.value }
            })}
            placeholder="www.example.com"
          />
          <TextField
            fullWidth
            label="Phone"
            value={settings.itineraryBranding?.phone || ''}
            onChange={(e) => setSettings({
              ...settings,
              itineraryBranding: { ...(settings.itineraryBranding || {}), phone: e.target.value }
            })}
            placeholder="+91 ..."
          />
        </Stack>
      </Paper>

      {user?.role === ROLES.SUPER_ADMIN && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Roster planning defaults
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Default suggested work and off day counts for the Crew Duty <strong>Rostering</strong> tab (ops can change per create).
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="number"
              label="Planned work days (default)"
              value={settings.rosterPlanningDefaults?.plannedWorkDays ?? 20}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setSettings({
                  ...settings,
                  rosterPlanningDefaults: {
                    ...settings.rosterPlanningDefaults,
                    plannedWorkDays: Number.isFinite(v) ? Math.min(365, Math.max(1, v)) : 20
                  }
                });
              }}
              inputProps={{ min: 1, max: 365 }}
              size="small"
              sx={{ maxWidth: 280 }}
            />
            <TextField
              type="number"
              label="Planned off days (default)"
              value={settings.rosterPlanningDefaults?.plannedOffDays ?? 10}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setSettings({
                  ...settings,
                  rosterPlanningDefaults: {
                    ...settings.rosterPlanningDefaults,
                    plannedOffDays: Number.isFinite(v) ? Math.min(365, Math.max(0, v)) : 10
                  }
                });
              }}
              inputProps={{ min: 0, max: 365 }}
              size="small"
              sx={{ maxWidth: 280 }}
            />
          </Stack>
        </Paper>
      )}
        </>
      )}

      {/* Measurement Units Tab */}
      {tabValue === 1 && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Unit Preference
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure how measurement units are applied across the system. System-wide settings apply to all users.
              Per User allows individual user preferences. Per Aircraft uses aircraft-specific settings (default).
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Unit Preference</InputLabel>
              <Select
                value={settings.measurementUnits?.preference || 'PER_AIRCRAFT'}
                label="Unit Preference"
                onChange={(e) => setSettings({
                  ...settings,
                  measurementUnits: {
                    ...settings.measurementUnits,
                    preference: e.target.value
                  }
                })}
              >
                <MenuItem value="SYSTEM_WIDE">System-wide</MenuItem>
                <MenuItem value="PER_USER">Per User</MenuItem>
                <MenuItem value="PER_AIRCRAFT">Per Aircraft (Default)</MenuItem>
              </Select>
            </FormControl>

            {settings.measurementUnits?.preference === 'SYSTEM_WIDE' && (
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                  System-wide Unit Settings
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Fuel Unit</InputLabel>
                      <Select
                        value={settings.measurementUnits?.systemWide?.FUEL || 'kg'}
                        label="Fuel Unit"
                        onChange={(e) => setSettings({
                          ...settings,
                          measurementUnits: {
                            ...settings.measurementUnits,
                            systemWide: {
                              ...settings.measurementUnits.systemWide,
                              FUEL: e.target.value
                            }
                          }
                        })}
                      >
                        <MenuItem value="kg">kg</MenuItem>
                        <MenuItem value="lb">lb</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Altitude Unit</InputLabel>
                      <Select
                        value={settings.measurementUnits?.systemWide?.ALTITUDE || 'ft'}
                        label="Altitude Unit"
                        onChange={(e) => setSettings({
                          ...settings,
                          measurementUnits: {
                            ...settings.measurementUnits,
                            systemWide: {
                              ...settings.measurementUnits.systemWide,
                              ALTITUDE: e.target.value
                            }
                          }
                        })}
                      >
                        <MenuItem value="ft">ft</MenuItem>
                        <MenuItem value="m">m</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Temperature Unit</InputLabel>
                      <Select
                        value={settings.measurementUnits?.systemWide?.TEMPERATURE || '°C'}
                        label="Temperature Unit"
                        onChange={(e) => setSettings({
                          ...settings,
                          measurementUnits: {
                            ...settings.measurementUnits,
                            systemWide: {
                              ...settings.measurementUnits.systemWide,
                              TEMPERATURE: e.target.value
                            }
                          }
                        })}
                      >
                        <MenuItem value="°C">°C</MenuItem>
                        <MenuItem value="°F">°F</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Pressure Unit</InputLabel>
                      <Select
                        value={settings.measurementUnits?.systemWide?.PRESSURE || 'PSI'}
                        label="Pressure Unit"
                        onChange={(e) => setSettings({
                          ...settings,
                          measurementUnits: {
                            ...settings.measurementUnits,
                            systemWide: {
                              ...settings.measurementUnits.systemWide,
                              PRESSURE: e.target.value
                            }
                          }
                        })}
                      >
                        <MenuItem value="PSI">PSI</MenuItem>
                        <MenuItem value="kPa">kPa</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Speed Unit</InputLabel>
                      <Select
                        value={settings.measurementUnits?.systemWide?.SPEED || 'knots'}
                        label="Speed Unit"
                        onChange={(e) => setSettings({
                          ...settings,
                          measurementUnits: {
                            ...settings.measurementUnits,
                            systemWide: {
                              ...settings.measurementUnits.systemWide,
                              SPEED: e.target.value
                            }
                          }
                        })}
                      >
                        <MenuItem value="knots">knots</MenuItem>
                        <MenuItem value="km/h">km/h</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

        </>
      )}

      {/* Nature of Flight Mapping Tab */}
      {tabValue === 2 && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Nature of Flight Mapping
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure the mapping between Nature of Flight codes and their descriptions. 
              These mappings are used in Technical Log Book entries.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Add New Mapping
              </Typography>
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <TextField
                  label="Code"
                  value={newNatureCode.code}
                  onChange={(e) => setNewNatureCode({ ...newNatureCode, code: e.target.value.toUpperCase() })}
                  size="small"
                  sx={{ width: 150 }}
                  inputProps={{ maxLength: 10 }}
                  helperText="e.g., N, TF, NF, GR"
                />
                <TextField
                  label="Description"
                  value={newNatureCode.description}
                  onChange={(e) => setNewNatureCode({ ...newNatureCode, description: e.target.value })}
                  size="small"
                  sx={{ flex: 1 }}
                  helperText="Full description of the flight type"
                />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddNatureMapping}
                  disabled={!newNatureCode.code.trim() || !newNatureCode.description.trim()}
                >
                  Add
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Current Mappings
              </Typography>
              {Object.keys(settings.measurementUnits?.natureOfFlightMapping || {}).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No mappings configured. Add a new mapping above.
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  {Object.entries(settings.measurementUnits?.natureOfFlightMapping || {}).map(([code, desc]) => (
                    <Chip
                      key={code}
                      label={`${code}: ${desc}`}
                      onDelete={() => handleRemoveNatureMapping(code)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Formatting Tab */}
      {tabValue === 3 && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Purchase Order Number Format
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Purchase order numbers are generated automatically when a PO is created. The format is fixed:
              operator code from master data, then the last three letters (A–Z only) of the aircraft registration,
              then the literal <strong>PO</strong>, the calendar year (YYYY), and a three-digit sequence (001, 002, …)
              that increments per operator, aircraft tail suffix, and year.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Pattern reference: {poPreview || '—'}
            </Typography>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Work Order Number Format
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure how Work Order numbers are generated. Add segments in order; an example is shown below.
            </Typography>
            <Stack spacing={2}>
              {workOrderFormat.map((segment, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <FormControl sx={{ minWidth: 260 }}>
                    <InputLabel>Segment type</InputLabel>
                    <Select
                      value={segment.type || 'PLAIN_TEXT'}
                      label="Segment type"
                      onChange={(e) => updateWorkOrderSegment(index, 'type', e.target.value)}
                    >
                      {WO_SEGMENT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {(segment.type === 'PLAIN_TEXT' || segment.type === 'USER_TEXT') && (
                    <TextField
                      label="Plain text"
                      value={segment.value ?? ''}
                      onChange={(e) => updateWorkOrderSegment(index, 'value', e.target.value)}
                      placeholder="e.g. WO-"
                      size="small"
                      sx={{ minWidth: 160 }}
                    />
                  )}
                  {segment.type === 'SEQUENCE' && (
                    <>
                      <TextField
                        type="number"
                        label="Start from"
                        value={segment.start ?? 1}
                        onChange={(e) => updateWorkOrderSegment(index, 'start', Math.max(0, parseInt(e.target.value, 10) || 0))}
                        inputProps={{ min: 0 }}
                        size="small"
                        sx={{ width: 120 }}
                      />
                      <TextField
                        type="number"
                        label="Number of digits"
                        value={segment.digits ?? 6}
                        onChange={(e) => updateWorkOrderSegment(index, 'digits', Math.min(8, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                        inputProps={{ min: 1, max: 8 }}
                        size="small"
                        sx={{ width: 140 }}
                        helperText="e.g. 4 for 0001"
                      />
                    </>
                  )}
                  <IconButton
                    color="error"
                    onClick={() => removeWorkOrderSegment(index)}
                    disabled={workOrderFormat.length <= 1}
                    title="Remove segment"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addWorkOrderSegment}
                size="small"
              >
                Add segment
              </Button>
              <Typography variant="subtitle2" color="text.secondary">
                Example: {getWorkOrderExamplePreview(workOrderFormat)}
              </Typography>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Serialized Item ID Format
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure how Serialized Item display IDs are generated (e.g. GA-SI-000001). Add segments in order; an example is shown below.
            </Typography>
            <Stack spacing={2}>
              {serializedItemFormat.map((segment, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <FormControl sx={{ minWidth: 260 }}>
                    <InputLabel>Segment type</InputLabel>
                    <Select
                      value={segment.type || 'PLAIN_TEXT'}
                      label="Segment type"
                      onChange={(e) => updateSerializedItemSegment(index, 'type', e.target.value)}
                    >
                      {SI_SEGMENT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {(segment.type === 'PLAIN_TEXT' || segment.type === 'USER_TEXT') && (
                    <TextField
                      label="Plain text"
                      value={segment.value ?? ''}
                      onChange={(e) => updateSerializedItemSegment(index, 'value', e.target.value)}
                      placeholder="e.g. GA-SI-"
                      size="small"
                      sx={{ minWidth: 160 }}
                    />
                  )}
                  {segment.type === 'SEQUENCE' && (
                    <>
                      <TextField
                        type="number"
                        label="Start from"
                        value={segment.start ?? 1}
                        onChange={(e) => updateSerializedItemSegment(index, 'start', Math.max(0, parseInt(e.target.value, 10) || 0))}
                        inputProps={{ min: 0 }}
                        size="small"
                        sx={{ width: 120 }}
                      />
                      <TextField
                        type="number"
                        label="Number of digits"
                        value={segment.digits ?? 6}
                        onChange={(e) => updateSerializedItemSegment(index, 'digits', Math.min(8, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                        inputProps={{ min: 1, max: 8 }}
                        size="small"
                        sx={{ width: 140 }}
                        helperText="e.g. 4 for 0001"
                      />
                    </>
                  )}
                  <IconButton
                    color="error"
                    onClick={() => removeSerializedItemSegment(index)}
                    disabled={serializedItemFormat.length <= 1}
                    title="Remove segment"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addSerializedItemSegment}
                size="small"
              >
                Add segment
              </Button>
              <Typography variant="subtitle2" color="text.secondary">
                Example: {getSerializedItemExamplePreview(serializedItemFormat)}
              </Typography>
            </Stack>
          </Paper>
        </>
      )}

      {tabValue === 4 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Live aircraft usage (Technical Log Book)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Live and current totals still come only from the latest <strong>approved</strong> Technical Log Book entry with
            non-empty carried-forward totals. This screen defines which <code>totalCarriedForward</code> field names are used
            for each Time Since Record unit when computing live due/remaining in Aircraft Life-cycle and related views.
            Per-aircraft overrides can be set on each aircraft (Warranty &amp; TSN tab).
          </Alert>
          {(() => {
            const raw = settings.liveTlbUsageMapping?.labelRules;
            const rules = Array.isArray(raw)
              ? raw.filter(
                  (r) =>
                    r &&
                    String(r.unit || '').trim() &&
                    String(r.label || '').trim() &&
                    String(r.tlbKey || '').trim()
                )
              : [];
            if (rules.length === 0) {
              return (
                <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
                  <strong>Label rules:</strong> None configured at tenant level. TSR rows use the unit → field mapping and
                  engine Hours rules below unless you add optional label rules.
                </Alert>
              );
            }
            return (
              <Alert severity="success" sx={{ mb: 2 }} variant="outlined">
                <Typography variant="subtitle2" gutterBottom>
                  Active label rules ({rules.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  First matching rule (unit + TSR label, case-insensitive) wins before the default unit mapping.
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0 }}>
                  {rules.map((r, i) => (
                    <li key={i}>
                      <Typography variant="body2" component="span">
                        <strong>{String(r.unit).trim()}</strong>
                        {' — label '}
                        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                          &quot;{String(r.label).trim()}&quot;
                        </Typography>
                        {' → '}
                        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {String(r.tlbKey).trim()}
                        </Typography>
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            );
          })()}
          <LiveTlbUsageMappingEditor
            value={settings.liveTlbUsageMapping}
            onChange={(next) => setSettings({ ...settings, liveTlbUsageMapping: next })}
          />
        </Paper>
      )}

      {tabValue === 5 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Hangar product (MRO + Store + Procurement)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            One client = one hangar stock. Outside CAMOs are Operators. Defaults keep the full suite unchanged until you enable flags.
            See SCOPE-Hangar-Product.md.
          </Alert>
          <Stack spacing={2} sx={{ maxWidth: 480 }}>
            <FormControl fullWidth>
              <InputLabel>Product mode</InputLabel>
              <Select
                label="Product mode"
                value={settings.hangar?.productMode || 'SUITE'}
                onChange={(e) => setSettings({
                  ...settings,
                  hangar: { ...(settings.hangar || {}), productMode: e.target.value }
                })}
              >
                <MenuItem value="SUITE">SUITE (full CAMO + Planning)</MenuItem>
                <MenuItem value="HANGAR">HANGAR (Store + Procurement + MRO)</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(settings.hangar?.enableMroFacade)}
                  onChange={(e) => setSettings({
                    ...settings,
                    hangar: { ...(settings.hangar || {}), enableMroFacade: e.target.checked }
                  })}
                />
              )}
              label="Enable MRO facade (/api/mro/queue)"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(settings.hangar?.enableExternalJobs)}
                  onChange={(e) => setSettings({
                    ...settings,
                    hangar: { ...(settings.hangar || {}), enableExternalJobs: e.target.checked }
                  })}
                />
              )}
              label="Enable external jobs (API ingest + manual create on /mro)"
            />
            <FormControl fullWidth>
              <InputLabel>External completion policy</InputLabel>
              <Select
                label="External completion policy"
                value={settings.hangar?.externalCompletionPolicy || 'AUTO_AFTER_MRO_QM'}
                onChange={(e) => setSettings({
                  ...settings,
                  hangar: { ...(settings.hangar || {}), externalCompletionPolicy: e.target.value }
                })}
              >
                <MenuItem value="AUTO_AFTER_MRO_QM">Auto-complete after MRO QM (no CAMO QM)</MenuItem>
                <MenuItem value="REQUIRE_EXTERNAL_CAMO_ACK">Require external CAMO ack API</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Operator API keys and webhook URLs: Master Data → Operators, then use{' '}
            <code>POST /api/mro/operators/:operatorId/api-keys</code> and{' '}
            <code>PUT /api/mro/operators/:operatorId/webhooks</code> (or the Hangar Operators panel below).
          </Typography>
          <HangarOperatorKeysPanel />
        </Paper>
      )}

      {tabValue === 6 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Staff details (ground staff)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Default off. When disabled, Staff details nav and suite APIs (roster, attendance, overtime, assignments)
            are unavailable; a separate Staff training page still provides ground-staff Certs &amp; Trainings.
            Day-to-day Ops / Crew Duty are unchanged. Existing Staff details data is retained. CREW (pilots) are
            excluded from Staff details. See SCOPE-HRMS.md.
          </Alert>
          <Stack spacing={2} sx={{ maxWidth: 480 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(settings.hrms?.enabled)}
                  onChange={(e) => setSettings({
                    ...settings,
                    hrms: { ...(settings.hrms || {}), enabled: e.target.checked }
                  })}
                />
              )}
              label="Enable Staff details"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(settings.hrms?.enforceManagerTeams)}
                  disabled={!settings.hrms?.enabled}
                  onChange={(e) => setSettings({
                    ...settings,
                    hrms: { ...(settings.hrms || {}), enforceManagerTeams: e.target.checked }
                  })}
                />
              )}
              label="Enforce manager teams (dept managers only manage assigned employees)"
            />
            <Alert severity="info" sx={{ py: 0.5 }}>
              Optional. When on, HR assigns managers and employees per department in Staff details → Manager teams.
              Department managers then only see and manage their assigned people.
            </Alert>
            <TextField
              label="Max daily OT hours"
              type="number"
              inputProps={{ min: 0, max: 24, step: 0.5 }}
              value={settings.hrms?.maxDailyOtHours ?? 4}
              onChange={(e) => setSettings({
                ...settings,
                hrms: { ...(settings.hrms || {}), maxDailyOtHours: Number(e.target.value) }
              })}
              fullWidth
            />
            <TextField
              label="Max weekly OT hours"
              type="number"
              inputProps={{ min: 0, max: 168, step: 0.5 }}
              value={settings.hrms?.maxWeeklyOtHours ?? 12}
              onChange={(e) => setSettings({
                ...settings,
                hrms: { ...(settings.hrms || {}), maxWeeklyOtHours: Number(e.target.value) }
              })}
              fullWidth
            />
          </Stack>
        </Paper>
      )}

      {tabValue === 7 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Safety integration API keys
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Super Admin only. Generate a non-expiring key for the client / external system to pull
            StandAlone WO, EDR inbox, and work-order details. Full key is visible for 2 minutes
            after generation only. No Safety-user login in Jupiter is required; view access is
            defined by the client system. Revoke or reissue to rotate.
            See SCOPE-Safety-Integration.md.
          </Alert>
          <SafetyApiKeysPanel />
        </Paper>
      )}

      {tabValue !== 7 && (
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          onClick={fetchSettings}
          disabled={saving}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
      )}
    </Box>
  );
};

export default ClientSettings;

